import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, HeartHandshake, PackageCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { api, formatINR } from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const HERO_IMG =
  "https://images.unsplash.com/photo-1644890916891-5a595c8c098f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxzdHVmZmVkJTIwYmFsbG9vbiUyMGhhbXBlcnxlbnwwfHx8fDE3ODM4MzczNTV8MA&ixlib=rb-4.1.0&q=85";

const CATEGORY_TILES = [
  {
    slug: "birthday",
    name: "Birthday",
    img: "https://customer-assets-cm19k8pv.emergentagent.net/job_balloon-story/artifacts/gsu3nfrd_Birthday.jpg",
    span: "md:col-span-8",
    aspect: "aspect-[16/10]",
  },
  {
    slug: "special-occasions",
    name: "Special Occasions",
    img: "https://customer-assets-cm19k8pv.emergentagent.net/job_balloon-story/artifacts/xz1txqh4_Special%20occasion.jpg",
    span: "md:col-span-4",
    aspect: "aspect-[4/5]",
  },
  {
    slug: "mothers-day",
    name: "Mother's Day",
    img: "https://images.unsplash.com/photo-1604072762229-9075f8878d30?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxlbGVnYW50JTIwZ2lmdHMlMjBmbG93ZXJzfGVufDB8fHx8MTc4MzgzNzM2Mnww&ixlib=rb-4.1.0&q=85",
    span: "hidden",
    aspect: "aspect-[4/5]",
    hidden: true,
  },
  {
    slug: "anniversary",
    name: "Anniversary",
    img: "https://customer-assets-cm19k8pv.emergentagent.net/job_balloon-story/artifacts/jvdb3knn_Anniversary.jpg",
    span: "md:col-span-12",
    aspect: "aspect-[21/8]",
  },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then(({ data }) => setFeatured(data)).catch(() => {});
  }, []);

  return (
    <div className="App">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 py-20 lg:grid-cols-2 lg:gap-20 lg:px-10 lg:py-28">
          <div className="relative z-10">
            <p className="label-tiny mb-6" data-testid="hero-eyebrow">
              <Sparkles className="mr-2 inline h-3.5 w-3.5" /> Handcrafted in India
            </p>
            <h1 className="font-serif text-4xl leading-[1.05] tracking-tight text-[#2a2928] sm:text-5xl lg:text-6xl" data-testid="hero-heading">
              Every moment,{" "}
              <span className="italic text-[#d08c8c]">wrapped in wonder.</span>
            </h1>
            <p className="mt-6 max-w-md text-base leading-relaxed text-[#7a7571] lg:text-lg">
              Handcrafted stuffed balloon hampers and customised chocolate
              wrappers — thoughtfully made for birthdays, weddings, corporate
              gifting and everything in between.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/shop"
                data-testid="hero-shop-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-[#d08c8c] px-8 py-3.5 text-sm font-medium text-white transition-colors hover:bg-[#b87a7a]"
              >
                Shop the collection
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/about"
                data-testid="hero-about-cta"
                className="inline-flex items-center gap-2 rounded-full border border-[#e8e2da] bg-white px-8 py-3.5 text-sm font-medium text-[#2a2928] transition-colors hover:border-[#d08c8c] hover:text-[#d08c8c]"
              >
                Our story
              </Link>
            </div>

            <div className="mt-14 grid max-w-md grid-cols-3 gap-4 border-t border-[#e8e2da] pt-8">
              <div>
                <div className="font-serif text-lg leading-tight text-[#2a2928]">Handcrafted</div>
                <div className="text-xs text-[#7a7571]">with love in Hassan</div>
              </div>
              <div>
                <div className="font-serif text-lg leading-tight text-[#2a2928]">Made to order</div>
                <div className="text-xs text-[#7a7571]">every hamper</div>
              </div>
              <div>
                <div className="font-serif text-lg leading-tight text-[#2a2928]">Scheduled</div>
                <div className="text-xs text-[#7a7571]">delivery</div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="relative overflow-hidden rounded-[2rem] shadow-[0_20px_60px_rgba(42,41,40,0.10)]">
              <img
                src={HERO_IMG}
                alt="Stuffed balloon hamper"
                className="h-full w-full object-cover aspect-[4/5]"
                data-testid="hero-image"
              />
              <div className="grain absolute inset-0" />
            </div>
            <div className="absolute -left-6 -bottom-6 hidden rounded-2xl border border-[#e8e2da] bg-white p-5 shadow-lg md:block">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-[#f4ede6] p-2">
                  <HeartHandshake className="h-5 w-5 text-[#d08c8c]" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#2a2928]">Personalised notes</div>
                  <div className="text-xs text-[#7a7571]">On every hamper.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-24" data-testid="categories-section">
        <div className="mb-12 flex items-end justify-between gap-6">
          <div>
            <p className="label-tiny mb-3">Shop by occasion</p>
            <h2 className="font-serif text-3xl text-[#2a2928] sm:text-4xl lg:text-5xl">Made for every moment.</h2>
          </div>
          <Link to="/shop" data-testid="categories-view-all" className="hidden text-sm font-medium text-[#d08c8c] hover:underline sm:inline-flex">
            View all →
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
          {CATEGORY_TILES.filter((c) => !c.hidden).map((c) => (
            <Link
              key={c.slug}
              to={`/shop?category=${c.slug}`}
              data-testid={`category-tile-${c.slug}`}
              className={`group relative overflow-hidden rounded-3xl ${c.span} card-lift`}
            >
              <div className={`${c.aspect} w-full overflow-hidden`}>
                <img
                  src={c.img}
                  alt={c.name}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#2a2928]/60 via-[#2a2928]/10 to-transparent" />
              <div className="absolute bottom-0 left-0 flex w-full items-end justify-between p-6 lg:p-8">
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] text-white/80">Occasion</p>
                  <h3 className="mt-1 font-serif text-2xl text-white lg:text-3xl">{c.name}</h3>
                </div>
                <ArrowRight className="h-5 w-5 text-white transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      {featured.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10" data-testid="featured-section">
          <div className="mb-12">
            <p className="label-tiny mb-3">Bestsellers</p>
            <h2 className="font-serif text-3xl text-[#2a2928] sm:text-4xl lg:text-5xl">
              Loved by hundreds of gifters.
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {featured.slice(0, 3).map((p) => (
              <Link
                to={`/product/${p.id}`}
                key={p.id}
                data-testid={`featured-product-${p.id}`}
                className="group card-lift"
              >
                <div className="relative overflow-hidden rounded-3xl border border-[#e8e2da] bg-white">
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={p.image_base64 || p.image_url}
                      alt={p.name}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                </div>
                <div className="mt-5 flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-serif text-xl text-[#2a2928]">{p.name}</h3>
                    <p className="mt-1 text-sm text-[#7a7571]">{p.short_description}</p>
                  </div>
                  <div className="text-right font-serif text-xl text-[#2a2928]">
                    {formatINR(p.price)}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* CHOCOLATES CTA */}
      <section className="mx-auto max-w-7xl px-6 pb-10 lg:px-10" data-testid="chocolates-cta-section">
        <Link
          to="/chocolates"
          data-testid="home-chocolates-cta"
          className="group relative flex flex-col items-start justify-between gap-6 overflow-hidden rounded-[2rem] border border-[#e8e2da] bg-white p-8 transition-colors hover:border-[#d08c8c] md:flex-row md:items-center md:p-12"
        >
          <div className="max-w-xl">
            <p className="label-tiny mb-3">New · shipped all-India</p>
            <h2 className="font-serif text-3xl text-[#2a2928] sm:text-4xl">
              Customised chocolate <span className="italic text-[#d08c8c]">wrappers.</span>
            </h2>
            <p className="mt-3 text-sm text-[#7a7571]">
              Personalised chocolates for corporate offices, hotels, resorts,
              weddings and every celebration. Ships pan-India in 5–7 days.
            </p>
            <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#d08c8c]">
              Explore chocolates <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          </div>
          <img
            src="https://customer-assets-cm19k8pv.emergentagent.net/job_balloon-story/artifacts/yd98k6ej_Customised%20chocolates.png"
            alt="Customised chocolates"
            className="h-40 w-full rounded-2xl object-cover md:h-56 md:w-72"
          />
        </Link>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
        <div className="rounded-[2rem] border border-[#e8e2da] bg-white p-10 lg:p-16">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <p className="label-tiny mb-3">How it works</p>
              <h2 className="font-serif text-3xl text-[#2a2928] sm:text-4xl lg:text-5xl">
                Simple, thoughtful,{" "}
                <span className="italic text-[#d08c8c]">on time.</span>
              </h2>
            </div>
            <div className="space-y-8">
              {[
                { n: "01", t: "Choose your hamper", d: "Pick from curated categories or request a custom design." },
                { n: "02", t: "Schedule your date", d: "Select the delivery date on our calendar — surprise timed to perfection." },
                { n: "03", t: "We craft & deliver", d: "Handmade with love, delivered fresh with a personal note." },
              ].map((s) => (
                <div key={s.n} className="flex items-start gap-6 border-t border-[#e8e2da] pt-6 first:border-0 first:pt-0">
                  <div className="font-serif text-2xl text-[#d08c8c]">{s.n}</div>
                  <div>
                    <h3 className="font-serif text-xl text-[#2a2928]">{s.t}</h3>
                    <p className="mt-1 text-sm text-[#7a7571]">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
